import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    Loader2,
    KeyRound,
    Eye,
    EyeOff,
    MessageCircleQuestion,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function ResetPassword() {
    const { resetPassword } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token");

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const mismatch = confirm.length > 0 && password !== confirm;
    const canSubmit = password.length >= 8 && password === confirm && !!token && !loading;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const data = await resetPassword(token, password);
            toast.success("Password aggiornata. Sei entrato in CondominioNow.");
            const u = data?.user;
            navigate(u?.role === "admin" ? "/admin" : "/dashboard");
        } catch (err) {
            setError(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-start justify-center bg-slate-50 px-4 py-14 sm:py-20">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center hover:opacity-80">
                        <img src="/brand-header.png" alt="CondominioNow" className="h-[60px] w-auto object-contain" />
                    </Link>
                </div>
                <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="space-y-1 pb-6">
                        <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                            <KeyRound className="size-5 text-primary" />
                        </div>
                        <h1 className="font-serif text-3xl font-semibold">Nuova password</h1>
                        <p className="text-sm text-muted-foreground">
                            Scegli una nuova password sicura per il tuo account.
                        </p>
                    </CardHeader>
                    <CardContent>
                        {!token ? (
                            <Alert variant="destructive" data-testid="reset-no-token">
                                <AlertTriangle className="size-4" />
                                <AlertDescription>
                                    Link non valido. Torna alla pagina di{" "}
                                    <Link
                                        to="/recupera-password"
                                        className="font-semibold underline"
                                    >
                                        recupero password
                                    </Link>
                                    .
                                </AlertDescription>
                            </Alert>
                        ) : (
                            <form
                                onSubmit={submit}
                                className="space-y-4"
                                data-testid="reset-form"
                                noValidate
                            >
                                {error && (
                                    <Alert variant="destructive" data-testid="reset-error">
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor="password">Nuova password</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPwd ? "text" : "password"}
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            minLength={8}
                                            data-testid="reset-password"
                                            placeholder="Almeno 8 caratteri"
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPwd(!showPwd)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            aria-label={
                                                showPwd ? "Nascondi password" : "Mostra password"
                                            }
                                        >
                                            {showPwd ? (
                                                <EyeOff className="size-4" />
                                            ) : (
                                                <Eye className="size-4" />
                                            )}
                                        </button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Almeno 8 caratteri, 1 lettera e 1 numero.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirm">Conferma password</Label>
                                    <Input
                                        id="confirm"
                                        type={showPwd ? "text" : "password"}
                                        required
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        data-testid="reset-confirm"
                                        placeholder="Ripeti la password"
                                    />
                                    {mismatch && (
                                        <p className="text-xs text-destructive">
                                            Le password non coincidono.
                                        </p>
                                    )}
                                </div>
                                <Button
                                    data-testid="reset-submit"
                                    type="submit"
                                    className="w-full h-11"
                                    disabled={!canSubmit}
                                >
                                    {loading && <Loader2 className="size-4 mr-2 animate-spin" />}
                                    Aggiorna password
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
